import { c as _c } from "react/compiler-runtime";
import { BulkChangeProjectAccessLevel } from "@components/table/projects/bulk-actions/bulk-change-project-access-level";
import { BulkChangeProjectManagement } from "@components/table/projects/bulk-actions/bulk-change-project-management";
import { BulkChangeProjectStatus } from "@components/table/projects/bulk-actions/bulk-change-project-status";
import { BulkDeleteProjects } from "@components/table/projects/bulk-actions/bulk-delete-projects";
import { BulkExportProjects } from "@components/table/projects/bulk-actions/bulk-export-projects";
import { useHasValidFeatureProjectLevel } from "@hooks/feature-control/use-has-user-valid-feature-project-level";
import { useHasUserValidPermissionProjectLevel } from "@hooks/permission-control/use-has-user-valid-permission-project-level";
import { useShouldShowBulkAction } from "@hooks/table/use-should-show-bulk-action";
import { isAllowedToChangeManagement } from "@pages/project-details/project-overview/change-management/change-management-utils";
import { UtilsTypes } from "@stellar/utils";
import { fetchingProjectsFlagsSelector } from "@store/projects/projects-selector";
import { useAppSelector } from "@store/store-helper";
import { selectedEntitiesSelector } from "@store/table/table-selector";
import { RequiredFeatureProjectLevelName } from "@utils/feature-control/project/project-feature-control-types";
import { RequiredPermissionProjectLevelName } from "@utils/permission-control/project-permission-control-types";
import { isProjectDemo } from "@utils/project-utils";
import { useMemo } from "react";
/** List all the bulk action components for project table */
export function useProjectsPageBulkActions(t0) {
  const $ = _c(59);
  const {
    companyId,
    projects,
    projectStatus,
    subjectType
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = selectedEntitiesSelector("projects");
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const selectedEntities = useAppSelector(t1);
  const {
    isFetchingProjects
  } = useAppSelector(fetchingProjectsFlagsSelector);
  const isAnyRowSelected = selectedEntities.length !== 0;
  const {
    hasUserPermissionProjectLevel
  } = useHasUserValidPermissionProjectLevel();
  const {
    hasFeatureProjectLevel
  } = useHasValidFeatureProjectLevel();
  const requiredRole = projectStatus === UtilsTypes.ProjectArchivingState.archived ? RequiredFeatureProjectLevelName.canUnarchiveProject : RequiredFeatureProjectLevelName.canArchiveProject;
  let t2;
  if ($[1] !== hasFeatureProjectLevel || $[2] !== requiredRole || $[3] !== selectedEntities) {
    let t3;
    if ($[5] !== hasFeatureProjectLevel || $[6] !== requiredRole) {
      t3 = project => hasFeatureProjectLevel({
        roleName: requiredRole,
        featuresAvailable: project.featuresAvailable ?? []
      });
      $[5] = hasFeatureProjectLevel;
      $[6] = requiredRole;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    t2 = selectedEntities.some(t3);
    $[1] = hasFeatureProjectLevel;
    $[2] = requiredRole;
    $[3] = selectedEntities;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const hasUserValidRoleToChangeStatusOfAnyProject = t2;
  let t3;
  if ($[8] !== hasUserPermissionProjectLevel || $[9] !== selectedEntities) {
    let t4;
    if ($[11] !== hasUserPermissionProjectLevel) {
      t4 = project_0 => hasUserPermissionProjectLevel({
        permissionName: RequiredPermissionProjectLevelName.canDeleteProject,
        project: project_0
      });
      $[11] = hasUserPermissionProjectLevel;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    t3 = selectedEntities.some(t4);
    $[8] = hasUserPermissionProjectLevel;
    $[9] = selectedEntities;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const hasUserValidRoleToDeleteAnyProject = t3;
  let t4;
  if ($[13] !== hasUserPermissionProjectLevel || $[14] !== selectedEntities) {
    let t5;
    if ($[16] !== hasUserPermissionProjectLevel) {
      t5 = project_1 => hasUserPermissionProjectLevel({
        permissionName: RequiredPermissionProjectLevelName.canChangeProjectAccessLevel,
        project: project_1
      });
      $[16] = hasUserPermissionProjectLevel;
      $[17] = t5;
    } else {
      t5 = $[17];
    }
    t4 = selectedEntities.some(t5);
    $[13] = hasUserPermissionProjectLevel;
    $[14] = selectedEntities;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const hasUserValidRoleToChangeAccessOfAnyProject = t4;
  let t5;
  if ($[18] !== selectedEntities) {
    t5 = selectedEntities.some(_temp);
    $[18] = selectedEntities;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const hasUserValidPermissionToChangeProjectGroupOfAnyProject = t5;
  let allowedButtons;
  if ($[20] !== hasUserValidPermissionToChangeProjectGroupOfAnyProject || $[21] !== hasUserValidRoleToChangeAccessOfAnyProject || $[22] !== hasUserValidRoleToChangeStatusOfAnyProject || $[23] !== hasUserValidRoleToDeleteAnyProject || $[24] !== isAnyRowSelected || $[25] !== projectStatus) {
    allowedButtons = ["export"];
    if (projectStatus === UtilsTypes.ProjectArchivingState.archived && isAnyRowSelected && hasUserValidRoleToDeleteAnyProject) {
      allowedButtons.push("removeProject");
    }
    if (isAnyRowSelected && hasUserValidRoleToChangeStatusOfAnyProject) {
      allowedButtons.push("changeProjectStatus");
    }
    if (isAnyRowSelected && hasUserValidPermissionToChangeProjectGroupOfAnyProject) {
      allowedButtons.push("changeProjectManagement");
    }
    if (projectStatus === UtilsTypes.ProjectArchivingState.active && isAnyRowSelected && hasUserValidRoleToChangeAccessOfAnyProject) {
      allowedButtons.push("changeAccessLevel");
    }
    $[20] = hasUserValidPermissionToChangeProjectGroupOfAnyProject;
    $[21] = hasUserValidRoleToChangeAccessOfAnyProject;
    $[22] = hasUserValidRoleToChangeStatusOfAnyProject;
    $[23] = hasUserValidRoleToDeleteAnyProject;
    $[24] = isAnyRowSelected;
    $[25] = projectStatus;
    $[26] = allowedButtons;
  } else {
    allowedButtons = $[26];
  }
  const allowedButtonsBasedOnRowSelection = allowedButtons;
  let t6;
  if ($[27] !== allowedButtonsBasedOnRowSelection) {
    t6 = {
      allowedButtonsBasedOnRowSelection
    };
    $[27] = allowedButtonsBasedOnRowSelection;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  const {
    shouldShowBulkButtons
  } = useShouldShowBulkAction(t6);
  let t7;
  if ($[29] !== isFetchingProjects || $[30] !== projects || $[31] !== shouldShowBulkButtons) {
    t7 = shouldShowBulkButtons("export") && (isFetchingProjects || projects.length > 0);
    $[29] = isFetchingProjects;
    $[30] = projects;
    $[31] = shouldShowBulkButtons;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  const shouldShowExportButton = t7;
  let t8;
  if ($[33] !== companyId || $[34] !== shouldShowBulkButtons) {
    t8 = shouldShowBulkButtons("changeProjectManagement") ? <BulkChangeProjectManagement companyId={companyId} /> : null;
    $[33] = companyId;
    $[34] = shouldShowBulkButtons;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  let t9;
  if ($[36] !== shouldShowBulkButtons) {
    t9 = shouldShowBulkButtons("changeAccessLevel") ? <BulkChangeProjectAccessLevel /> : null;
    $[36] = shouldShowBulkButtons;
    $[37] = t9;
  } else {
    t9 = $[37];
  }
  let t10;
  if ($[38] !== projectStatus || $[39] !== projects || $[40] !== shouldShowBulkButtons) {
    t10 = shouldShowBulkButtons("changeProjectStatus") ? <BulkChangeProjectStatus projectStatus={projectStatus} projects={projects} /> : null;
    $[38] = projectStatus;
    $[39] = projects;
    $[40] = shouldShowBulkButtons;
    $[41] = t10;
  } else {
    t10 = $[41];
  }
  let t11;
  if ($[42] !== companyId || $[43] !== shouldShowBulkButtons) {
    t11 = shouldShowBulkButtons("removeProject") ? <BulkDeleteProjects companyId={companyId} /> : null;
    $[42] = companyId;
    $[43] = shouldShowBulkButtons;
    $[44] = t11;
  } else {
    t11 = $[44];
  }
  let t12;
  if ($[45] !== companyId || $[46] !== isAnyRowSelected || $[47] !== projectStatus || $[48] !== projects || $[49] !== selectedEntities || $[50] !== shouldShowExportButton || $[51] !== subjectType) {
    t12 = shouldShowExportButton ? <BulkExportProjects companyId={companyId} selectedProjects={isAnyRowSelected ? selectedEntities : projects} subjectType={subjectType} projectStatus={projectStatus} areEntitiesSelected={isAnyRowSelected} /> : null;
    $[45] = companyId;
    $[46] = isAnyRowSelected;
    $[47] = projectStatus;
    $[48] = projects;
    $[49] = selectedEntities;
    $[50] = shouldShowExportButton;
    $[51] = subjectType;
    $[52] = t12;
  } else {
    t12 = $[52];
  }
  let t13;
  if ($[53] !== t10 || $[54] !== t11 || $[55] !== t12 || $[56] !== t8 || $[57] !== t9) {
    t13 = {
      changeProjectManagement: t8,
      changeAccessLevel: t9,
      changeProjectStatus: t10,
      removeProject: t11,
      export: t12
    };
    $[53] = t10;
    $[54] = t11;
    $[55] = t12;
    $[56] = t8;
    $[57] = t9;
    $[58] = t13;
  } else {
    t13 = $[58];
  }
  return t13;
}
function _temp(project_2) {
  return isAllowedToChangeManagement(isProjectDemo(project_2), project_2);
}